import { enhancePreparedOcrImage, type OcrTextImageEnhancement, type PreparedOcrImage } from '@/core/image'

/** Yellow ink on a dark panel. Geometry finds the ink; only OCR assigns digits. */
export function prepareYellowLevelImage(prepared: PreparedOcrImage, enhancement?: OcrTextImageEnhancement): PreparedOcrImage | null {
  const { source, sourceRect } = prepared
  const ink = new Uint8Array(source.width * source.height)
  let left = source.width, top = source.height, right = -1, bottom = -1
  const lights: number[] = []
  for (let y = 0; y < source.height; y++) for (let x = 0; x < source.width; x++) {
    const i = (y * source.width + x) * 4
    const r = source.data[i]!, g = source.data[i + 1]!, b = source.data[i + 2]!
    if (r < 110 || g < 100 || Math.min(r, g) - b < 60 || Math.abs(r - g) > 65) continue
    ink[y * source.width + x] = 1
    left = Math.min(left, x); right = Math.max(right, x)
    top = Math.min(top, y); bottom = Math.max(bottom, y)
    lights.push(.299 * r + .587 * g + .114 * b)
  }
  const width = right - left + 1, height = bottom - top + 1
  if (height < 5 || width < 2 || width / height > 3.8
    || left <= 0 || top <= 0 || right >= source.width - 1 || bottom >= source.height - 1) return null

  // Separate complete glyph columns, without depending on a particular level.
  const glyphs: { left: number; right: number; top: number; bottom: number; count: number }[] = []
  let glyph: typeof glyphs[number] | null = null
  for (let x = left; x <= right + 1; x++) {
    let lo = source.height, hi = -1, count = 0
    for (let y = top; y <= bottom; y++) if (ink[y * source.width + x]) { lo = Math.min(lo, y); hi = y; count++ }
    if (count) {
      glyph ??= { left: x, right: x, top: lo, bottom: hi, count: 0 }
      glyph.right = x; glyph.top = Math.min(glyph.top, lo); glyph.bottom = Math.max(glyph.bottom, hi); glyph.count += count
    } else if (glyph) { glyphs.push(glyph); glyph = null }
  }
  if (!glyphs.length || glyphs.length > 3) return null
  for (const [index, g] of glyphs.entries()) {
    const w = g.right - g.left + 1, h = g.bottom - g.top + 1
    if (h < height * .75 || w < height * .12 || w > height * 1.15
      || g.count < w * h * .15 || g.count > w * h * .85
      || index > 0 && g.left - glyphs[index - 1]!.right - 1 > height * .65) return null
  }

  // Normalize luminance, not a binary color mask: capture subsampling changes
  // chroma at the stroke edges, while their luminance still describes the glyph.
  const samples: number[] = []
  let dark = 0
  for (let y = top - 1; y <= bottom + 1; y++) for (let x = left - 1; x <= right + 1; x++) {
    const i = (y * source.width + x) * 4
    const l = .299 * source.data[i]! + .587 * source.data[i + 1]! + .114 * source.data[i + 2]!
    samples.push(l)
    if (l < 100) dark++
  }
  samples.sort((a, b) => a - b); lights.sort((a, b) => a - b)
  const low = samples[Math.floor(samples.length * .2)]!, high = lights[Math.floor(lights.length * .9)]!
  if (dark < samples.length * .25 || high - low < 60) return null
  const alpha = (x: number, y: number) => {
    const i = (y * source.width + x) * 4
    return Math.max(0, Math.min(1, (.299 * source.data[i]! + .587 * source.data[i + 1]! + .114 * source.data[i + 2]! - low) / (high - low)))
  }
  // Do not manufacture a clean boundary around strokes clipped by the ROI.
  for (let x = left; x <= right; x++) if (top === 1 && alpha(x, 0) > .2
    || bottom === source.height - 2 && alpha(x, source.height - 1) > .2) return null
  for (let y = top; y <= bottom; y++) if (left === 1 && alpha(0, y) > .2
    || right === source.width - 2 && alpha(source.width - 1, y) > .2) return null

  // A label to the left may be LV.; text to the right belongs to another field.
  for (let x = right + 2; x < source.width; x++) {
    let bright = 0
    for (let y = top; y <= bottom; y++) if (alpha(x, y) > .75) bright++
    if (bright >= Math.max(3, height * .4)) return null
  }
  const margin = 3, paddedWidth = width + margin * 2, paddedHeight = height + margin * 2
  const data = new Uint8ClampedArray(paddedWidth * paddedHeight * 4).fill(255)
  const original = new Uint8ClampedArray(width * height * 4)
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const i = ((y + top) * source.width + x + left) * 4
    original.set(source.data.subarray(i, i + 4), (y * width + x) * 4)
    const out = ((y + margin) * paddedWidth + x + margin) * 4
    data[out] = data[out + 1] = data[out + 2] = 255 * (1 - alpha(x + left, y + top))
  }
  const mask = new ImageData(data, paddedWidth, paddedHeight)
  const enhanced = enhancePreparedOcrImage({ ...prepared, source: mask, processed: mask }, enhancement)
  const scaleX = enhanced.transform.contentWidth / paddedWidth, scaleY = enhanced.transform.contentHeight / paddedHeight
  return {
    source: new ImageData(original, width, height), processed: enhanced.processed,
    sourceRect: { x: sourceRect.x + left / source.width * sourceRect.width,
      y: sourceRect.y + top / source.height * sourceRect.height,
      width: width / source.width * sourceRect.width, height: height / source.height * sourceRect.height },
    transform: { contentX: enhanced.transform.contentX + margin * scaleX, contentY: enhanced.transform.contentY + margin * scaleY,
      contentWidth: width * scaleX, contentHeight: height * scaleY },
  }
}
